import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Paper,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LessonPlanRenderer from '../components/lesson-plan/LessonPlanRenderer';
import MarkdownEditor from '../components/MarkdownEditor';
import ResponseGraphs from '../components/ResponseGraphs';
import ApiClient from '../config/ApiClient';
import { CoachContent } from '../object-actions/types/types';
import { ThemeContext } from '../theme/ThemeContext';
import {
    getDeliveryStatus,
    markAthleteReceived,
    markParentReceived,
    publishCoachContent,
    regenerateWithChanges
} from '../utils/contentHelpers';

interface CoachContentScreenProps {
    entity: CoachContent;
}

const CoachContentEditorView: React.FC<CoachContentScreenProps> = ({
    entity
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { enqueueSnackbar } = useSnackbar();
    const { brandingSettings } = useContext(ThemeContext);

    // State for editorial features
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
    const [changeRequest, setChangeRequest] = useState('');

    // Edit form state
    const [editTitle, setEditTitle] = useState(entity.title);
    const [editBody, setEditBody] = useState(entity.body);

    // Check for edit query parameter on mount
    useEffect(() => {
        const editParam = searchParams.get('edit');
        if (editParam === 'true') {
            setIsEditMode(true);
        }
    }, [searchParams]);

    // Sync edit form state when entity prop changes (e.g., after navigation to new version)
    useEffect(() => {
        setEditTitle(entity.title);
        setEditBody(entity.body);
        // Only reset edit mode when entity changes if not coming from query param
        const editParam = searchParams.get('edit');
        if (editParam !== 'true') {
            setIsEditMode(false);
        }
    }, [entity.id, entity.title, entity.body, searchParams]);

    // Delivery status
    const deliveryStatus = getDeliveryStatus(entity);


    // Mark as received when athlete or parent accesses the page (only if not already marked)
    useEffect(() => {
        const markAsReceived = async () => {
            try {
                if (entity.my_roles?.includes('athlete') && !entity.athlete_received) {
                    await markAthleteReceived(Number(entity.id));
                } else if (entity.my_roles?.includes('parent') && !entity.parent_received) {
                    await markParentReceived(Number(entity.id));
                }
            } catch (error) {
                console.error('Error marking content as received:', error);
                // Don't show error to user as this is a background operation
            }
        };

        markAsReceived();
    }, [entity.my_roles, entity.id, entity.athlete_received, entity.parent_received]);

    if (!entity || entity._type !== 'CoachContent') return <LinearProgress />;

    const handlePublish = async () => {
        setLoading(true);
        try {
            await publishCoachContent(Number(entity.id));
            enqueueSnackbar('Content published successfully!', { variant: 'success' });
            // Refresh the page to show updated status
            window.location.reload();
        } catch (error) {
            enqueueSnackbar('Failed to publish content', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Create a new version instead of updating (updates are disabled)
            const response = await ApiClient.post('/api/coach-content/', {
                title: editTitle,
                body: editBody, // Save as Markdown directly
                submission: entity.submission?.id || null,
                assignment: entity.assignment?.id || null,
                athlete: entity.athlete?.id || null,
                assessment: entity.assessment?.id || null,
                purpose: entity.purpose,
            });

            if (response.error) {
                throw new Error(response.error);
            }

            enqueueSnackbar('New version created successfully!', { variant: 'success' });
            setIsEditMode(false);
            // Navigate to the new version
            const newContent = response.data as CoachContent;
            if (newContent?.id) {
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                // Fallback: refresh the page
                window.location.reload();
            }
        } catch (error) {
            enqueueSnackbar('Failed to save content', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateDraft = async () => {
        if (!changeRequest.trim()) return;

        setLoading(true);
        try {
            // Call the regenerate_with_changes endpoint to create a new version
            const newContent = await regenerateWithChanges(Number(entity.id), changeRequest);

            setRegenerateDialogOpen(false);
            setChangeRequest('');
            enqueueSnackbar('New version created successfully!', { variant: 'success' });

            // Navigate to the new version
            if (newContent?.id) {
                setIsEditMode(false);
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                // Fallback: refresh the page
                window.location.reload();
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to regenerate content';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRepublishCurriculum = async () => {
        if (!entity.source_content?.id) return;

        setLoading(true);
        try {
            const newContent = await publishCoachContent(Number(entity.source_content.id));
            enqueueSnackbar('Curriculum republished successfully! Lesson plan will be regenerated.', { variant: 'success' });
            // Navigate to the new version
            if (newContent?.id) {
                setIsEditMode(false);
                navigate(`/coach-content/${newContent.id}/`);
            } else {
                navigate(`/progress-report/${entity.athlete?.id}/`);
            }
        } catch (error) {
            enqueueSnackbar('Failed to republish curriculum', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                {/* Alert for failed lesson plan with source content */}
                {entity.purpose === 'lesson_plan' && !entity.json_body && entity.source_content && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 3 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleRepublishCurriculum}
                                disabled={loading}
                            >
                                {loading ? 'Republishing...' : 'Republish Curriculum'}
                            </Button>
                        }
                    >
                        This lesson plan failed to generate properly. You can republish the curriculum to regenerate it.
                    </Alert>
                )}

                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                mb: 2
                            }}
                        >
                            {isEditMode ? (
                                <TextField
                                    fullWidth
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    variant="outlined"
                                    size="medium"
                                />
                            ) : (
                                entity.title
                            )}
                        </Typography>

                        {deliveryStatus.coachDelivered && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`Coach Delivered: ${deliveryStatus.coachDelivered ? 'Yes' : 'No'}`}
                                    color={deliveryStatus.coachDelivered ? 'success' : 'default'}
                                    size="small"
                                />
                                <Chip
                                    label={`Athlete Received: ${deliveryStatus.athleteReceived ? 'Yes' : 'No'}`}
                                    color={deliveryStatus.athleteReceived ? 'success' : 'default'}
                                    size="small"
                                />
                                <Chip
                                    label={`Guardian Received: ${deliveryStatus.parentReceived ? 'Yes' : 'No'}`}
                                    color={deliveryStatus.parentReceived ? 'success' : 'default'}
                                    size="small"
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        {!isEditMode ? (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setIsEditMode(true)}
                                >
                                    Edit
                                </Button>
                                {!deliveryStatus.isPublished && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handlePublish}
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress color="secondary" size={20} /> : undefined}
                                    >
                                        Publish
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setRegenerateDialogOpen(true)}
                                    sx={{ ml: 1 }}
                                >
                                    Request Changes
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setIsEditMode(false)}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Box>


                </Box>

                {/* ResponseGraphs Section */}
                {entity.athlete && (
                    <ResponseGraphs
                        entity={entity.athlete}
                        height={300}
                    />
                )}

                {/* Content Body */}
                <Box sx={{ mb: 2, mt: 2 }}>
                    {isEditMode ? (
                        <Box>
                            <MarkdownEditor
                                value={editBody}
                                onChange={setEditBody}
                                placeholder="Enter your content here..."
                                minHeight={400}
                            />
                        </Box>
                    ) : (
                        <Box>
                            {entity.purpose === 'lesson_plan' && entity.json_body ? (
                                <LessonPlanRenderer entity={entity} />
                            ) : entity.body ? (
                                <Box
                                    sx={{
                                        '& p': { margin: 0 },
                                        '& p + p': { marginTop: 2 },
                                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                                            marginTop: 3,
                                            marginBottom: 2,
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            fontFamily: brandingSettings?.typography?.fontFamily || theme.typography.fontFamily
                                        },
                                        '& ul, & ol': {
                                            marginTop: 2,
                                            marginBottom: 2,
                                            paddingLeft: 3
                                        },
                                        '& li': {
                                            marginBottom: 1
                                        },
                                        '& a': {
                                            color: theme.palette.primary.main,
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        },
                                        '& blockquote': {
                                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                                            paddingLeft: 2,
                                            marginLeft: 0,
                                            fontStyle: 'italic',
                                            color: 'text.secondary',
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                                            padding: 2,
                                            borderRadius: 1
                                        },
                                        '& code': {
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                            padding: '2px 4px',
                                            borderRadius: 1,
                                            fontFamily: 'monospace'
                                        },
                                        '& pre': {
                                            backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                            padding: 2,
                                            borderRadius: 1,
                                            overflow: 'auto'
                                        }
                                    }}
                                >
                                    <ReactMarkdown>{entity.body}</ReactMarkdown>
                                </Box>
                            ) : (
                                <Typography variant="body1" color="text.secondary">
                                    No content available at this time.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Screenshots Section */}
                {entity.purpose !== 'lesson_plan' && (entity.screenshot_light || entity.screenshot_dark) && (
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="h5"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                mb: 3
                            }}
                        >
                            Screenshots
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {entity.screenshot_light && (
                                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                                        Light Theme
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: 2
                                        }}
                                    >
                                        <img
                                            src={entity.screenshot_light}
                                            alt="Light theme screenshot"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => entity.screenshot_light && window.open(entity.screenshot_light, '_blank')}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => entity.screenshot_light && window.open(entity.screenshot_light, '_blank')}
                                        >
                                            View Full Size
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                if (entity.screenshot_light) {
                                                    const link = document.createElement('a');
                                                    link.href = entity.screenshot_light;
                                                    link.download = `coach_content_${entity.id}_light.png`;
                                                    link.click();
                                                }
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {entity.screenshot_dark && (
                                <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                                    <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                                        Dark Theme
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: 2
                                        }}
                                    >
                                        <img
                                            src={entity.screenshot_dark}
                                            alt="Dark theme screenshot"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => entity.screenshot_dark && window.open(entity.screenshot_dark, '_blank')}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => entity.screenshot_dark && window.open(entity.screenshot_dark, '_blank')}
                                        >
                                            View Full Size
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                if (entity.screenshot_dark) {
                                                    const link = document.createElement('a');
                                                    link.href = entity.screenshot_dark;
                                                    link.download = `coach_content_${entity.id}_dark.png`;
                                                    link.click();
                                                }
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Regenerate Draft Dialog */}
                <Dialog
                    open={regenerateDialogOpen}
                    onClose={() => setRegenerateDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Request Changes</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Change Request"
                            placeholder="Describe what changes you'd like..."
                            fullWidth
                            multiline
                            rows={4}
                            value={changeRequest}
                            onChange={(e) => setChangeRequest(e.target.value)}
                            variant="outlined"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRegenerateDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRegenerateDraft}
                            variant="contained"
                            disabled={!changeRequest.trim() || loading}
                        >
                            {loading ? <CircularProgress size={20} /> : 'Request Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default CoachContentEditorView;
